/**
 * The browser VM: a real Linux, emulated in WebAssembly, inside the tab.
 *
 * This package owns the emulator and nothing else. It boots Alpine from a
 * saved state, hands out an exec channel and a terminal, and says when a
 * command has finished. It knows nothing about missions, grading or React;
 * `BrowserWorkspace` sits on top of it and satisfies the engine's
 * `Workspace` interface with the same three primitives the Docker driver
 * uses (`cat`, `cat >`, one `find`).
 *
 * The design and its measurements are docs/browser.md; the proven code this
 * is a port of is spike/web/ and spike/image/.
 *
 * ## Three serial lines
 *
 * The guest has three serial lines, wired by /etc/inittab and the learner's
 * profile in the image (spike/image/inittab, spike/image/bash_profile):
 *
 *   ttyS0  the learner's terminal. `agetty -a engineer` respawns a login
 *          shell that `cd`s to the level's working directory (read from
 *          /run/eos/cwd) and sources the same sandbox/bashrc both server
 *          drivers use. `attachTerminal` pumps bytes between it and xterm.
 *          Killing it (`pkill -HUP -t ttyS0`) is how a level change moves the
 *          prompt: inittab respawns it in the new directory.
 *
 *   ttyS1  the exec channel. `eosd` (spike/image/eosd) runs there as the
 *          learner and answers one framed request at a time. This is the
 *          line `exec()` uses, and therefore the line every `Workspace`
 *          call, the git graph and the grader travel over.
 *
 *   ttyS2  the command-done marker. The learner's `PROMPT_COMMAND` is
 *          `printf x > /dev/ttyS2`, so one byte arrives each time an
 *          interactive command finishes. That byte is the graph's refresh
 *          trigger, replacing the server's 250 ms and 1200 ms guesses. Its
 *          value is meaningless; only its arrival matters.
 *
 * ## The wire format on ttyS1
 *
 * One line per message, payloads base64 without wrapping, so a single
 * `read` frames a request and nothing in a script or in its output can be
 * mistaken for the protocol. Verbatim from spike/image/eosd:
 *
 *   host -> guest   EOS1 <id> <cwd-b64> <env-b64|-> <script-b64> <stdin-b64|->
 *   guest -> host   EOSR <id> <code> <stdout-b64> <stderr-b64> <capped>
 *
 * Both lines end with `\n` (the guest's tty is raw, so `\r` may still
 * appear before it and is stripped). `<id>` is an opaque token chosen by
 * the host that the reply echoes; ids beginning with `p` are readiness
 * probes, sent outside the request queue, whose replies carry no result.
 * `<env-b64>` decodes to `export K='v'` lines with single quotes escaped,
 * which the agent sources with `set -a`. `-` means "empty" in the env and
 * stdin fields. `<code>` is the decimal exit status, 124 when the guest's
 * own `timeout` fired. `<capped>` is `-`, `o`, `e` or `oe`: which streams
 * the agent cut at its 1 MiB output cap, so a reply always fits in one line
 * a host can buffer. An image built before that field simply omits it.
 *
 * On a cold boot, and only then, the agent announces itself with a bare
 * line `EOSREADY` before its first request. A restored guest never does:
 * it was already running when the state was saved.
 *
 * Anything on ttyS1 that is not `EOSREADY` and does not start with
 * `EOSR ` is kernel noise from before the agent started, and is dropped.
 */
/* --- Constants the guest and the host must agree on --------------------- */
/**
 * Serial port indices, as passed to v86's `serial_send_bytes` and read from
 * its `serial<N>-output-byte` events.
 */
export const SERIAL_TERMINAL = 0;
export const SERIAL_EXEC = 1;
export const SERIAL_COMMAND_DONE = 2;
/** The wire (see the module comment). */
export const REQUEST_MAGIC = 'EOS1';
export const REPLY_MAGIC = 'EOSR';
export const READY_LINE = 'EOSREADY';
/** The spike's default, and roughly the server's. */
export const DEFAULT_EXEC_TIMEOUT_MS = 30_000;
/**
 * The agent runs every script under `timeout 120` and the wire has no field
 * for a per-request timeout, so a longer host timeout would wait for a
 * reply the guest has already stopped trying to produce. A caller that
 * needs more than this needs the protocol to grow a field first.
 */
export const MAX_EXEC_TIMEOUT_MS = 120_000;
/**
 * The most one request may take on the wire: magic, id, and the four base64
 * payloads, spaces and newline included.
 *
 * The serial line has no flow control and the agent reads a request with
 * bash's `read`, one `read(2)` per byte. Measured against the real image, a
 * request line of about 131 KB (96 KiB of stdin) arrives intact and one of
 * about 175 KB (128 KiB of stdin) stops ttyS1 permanently: the guest emits
 * nothing ever again, a bare newline does not revive it, `ready()` fails,
 * and because everything above the workspace seam travels over this line —
 * grading, the git graph, the file tree, loading a level — the tab is dead
 * while the learner's shell on ttyS0 still echoes.
 *
 * So a request past this size is refused rather than sent, at a third of the
 * largest size measured safe. A caller with more to say splits it into
 * several requests, and the reply to each is the flow control the wire does
 * not have: `BrowserWorkspace.writeFile` writes a large file as a series of
 * appends. The threshold is a measurement, not a specification, and it has
 * only been taken in Node against v86 0.5.458; it is deliberately far below
 * it.
 */
export const MAX_REQUEST_BYTES = 64 * 1024;
/** Guest paths the image fixes (spike/image/init.sh, bake-levels.sh). */
export const WORKSPACE_ROOT = '/workspace';
export const LEVELS_DIR = '/levels';
/** Where the host writes the directory the next ttyS0 shell starts in. */
export const CWD_FILE = '/run/eos/cwd';
//# sourceMappingURL=types.js.map